/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable } from '@angular/core';
import { combineLatest, interval, Observable } from 'rxjs';
import { distinct, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { ctcTimeUpdate, fetchCtcTime } from '../../configuration/+state/configuration.actions';
import { selectPrimaryServerTimeOffset } from '../../configuration/+state/configuration.selectors';
import dayjs, { Dayjs } from 'dayjs';
import { ClusterUrlProviderService } from './cluster-url-provider.service';
import { StompBaseConsumer } from '../abstract/stomp-base-consumer';
import { StompManagerService } from './stomp-manager/stomp-manager.service';
import { Topic } from '../../user/model/multiple-subscriptions';

@Injectable({
    providedIn: 'root'
})
export class TimerService extends StompBaseConsumer {
    private readonly SYNC_SERVER_TIME_URI = `/api/sync-server-time`;

    public counter$ = interval(1000).pipe(startWith(0));
    public offset$: Observable<number>;
    public dayJsServerTime$: Observable<Dayjs>;
    public serverTime$: Observable<string>;
    public serverDate$: Observable<string>;

    constructor(
        private http: HttpClient,
        private clusterUrlProviderService: ClusterUrlProviderService,
        protected store: Store,
        protected stompManager: StompManagerService
    ) {
        super(stompManager, store);
        this.offset$ = this.store.select(selectPrimaryServerTimeOffset).pipe(distinctUntilChanged());
        this.dayJsServerTime$ = combineLatest([this.counter$, this.offset$]).pipe(map(([, offset]) => dayjs().add(offset, 'ms')));
        this.serverTime$ = this.dayJsServerTime$.pipe(
            map((time) => time.format('HH:mm:ss')),
            distinct()
        );
        this.serverDate$ = this.dayJsServerTime$.pipe(
            map((time) => time.format('ddd, MMM D')),
            distinct()
        );
    }

    protected handleCtcTimeUpdateMessage(message: any, clusterName?: string) {
        this.store.dispatch(ctcTimeUpdate({ clusterName: clusterName, offset: dayjs(message).diff(dayjs()) }));
    }

    protected getTopics(): Topic[] {
        return [Topic.SYNC_SERVER_TIME];
    }

    protected getTopicHandlers(): { [p in Topic]?: (message: any) => void } {
        return {
            [Topic.SYNC_SERVER_TIME]: this.handleCtcTimeUpdateMessage.bind(this)
        };
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Server time topic ${topic} subscription success: ${receiptId}`);
        this.store.dispatch(fetchCtcTime({ clusterName }));
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Server time topic ${topic} subscription end: ${receiptId}`);
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Server time topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    public requestServerTime(clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName);
        return this.http.get<number>(`${url}${this.SYNC_SERVER_TIME_URI}`, { observe: 'body', responseType: 'json' });
    }
}
